import {
    collection,
    doc,
    runTransaction,
    serverTimestamp,
    addDoc,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Returns true if the form's deadline has passed.
 * @param {string|null|undefined} deadline - ISO datetime string stored on the form doc
 */
function isDeadlinePassed(deadline) {
    if (!deadline) return false;
    return new Date() > new Date(deadline);
}

/**
 * Submits a registration and atomically decrements spotsLeft
 * for each selected option using a Firestore transaction.
 * Optionally sends a confirmation email via the Firebase "Trigger Email" extension.
 *
 * @param {string} formId - the ID of the form
 * @param {Object} formData - { firstName, lastName, email, phone }
 * @param {string[]} selectedOptionIds - array of option document IDs
 * @param {Object|null} emailConfig - { enabled, to, subject, message, name }
 * @returns {Promise<string>} - the new registration document ID
 * @throws {Error} - if any selected option has no spots left
 */
export async function submitRegistration(formId, formData, selectedOptionIds, emailConfig = null) {
    if (!formId) throw new Error("Missing formId");

    // Use a transaction to atomically check & decrement all selected options
    const registrationId = await runTransaction(db, async (transaction) => {
        // 1. Check deadline by reading the form document inside the transaction
        const formRef = doc(db, "forms", formId);
        const formSnap = await transaction.get(formRef);
        if (formSnap.exists()) {
            const formConfig = formSnap.data();
            if (formConfig.startTime && new Date() < new Date(formConfig.startTime)) {
                throw new Error("Formularz nie jest jeszcze dostępny.");
            }
            if (isDeadlinePassed(formConfig.deadline)) {
                throw new Error("Termin zgłoszeń minął. Rejestracja jest zamknięta.");
            }
        }

        // 2. Read all selected option docs inside the transaction
        const optionRefs = selectedOptionIds.map((id) => doc(db, "forms", formId, "options", id));
        const optionSnaps = await Promise.all(
            optionRefs.map((ref) => transaction.get(ref))
        );

        // 3. Validate that all options have spots left
        for (let i = 0; i < optionSnaps.length; i++) {
            const snap = optionSnaps[i];
            if (!snap.exists()) {
                throw new Error(`Opcja "${selectedOptionIds[i]}" nie istnieje.`);
            }
            const data = snap.data();
            if (data.spotsLeft <= 0) {
                throw new Error(
                    `Przepraszamy, "${data.name}" nie ma już wolnych miejsc. Odśwież stronę i spróbuj ponownie.`
                );
            }
        }

        // 4. Decrement spotsLeft for each selected option
        for (let i = 0; i < optionRefs.length; i++) {
            const currentSpots = optionSnaps[i].data().spotsLeft;
            transaction.update(optionRefs[i], { spotsLeft: currentSpots - 1 });
        }

        // 5. Create the registration document
        const regRef = doc(collection(db, "forms", formId, "registrations"));
        transaction.set(regRef, {
            ...formData,
            selectedOptions: selectedOptionIds,
            createdAt: serverTimestamp(),
        });

        return regRef.id;
    });

    // 5. Send confirmation email if configured
    if (emailConfig?.enabled && emailConfig?.to && emailConfig?.message) {
        const personalizedMessage = emailConfig.message.replace(
            /\{\{imie\}\}/g,
            emailConfig.name || ""
        );

        const imageUrls = emailConfig.imageUrls || [];

        // Build HTML email with inline images after the message text
        const htmlImages = imageUrls
            .filter((url) => url?.trim())
            .map((url) => `<img src="${url}" alt="" style="max-width:100%;height:auto;display:block;margin:12px 0;border-radius:8px;" />`)
            .join("\n");

        const htmlMessage = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /></head>
<body style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#2d2a26;">
  <div style="white-space:pre-line;line-height:1.6;font-size:15px;">${personalizedMessage.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
  ${htmlImages}
</body>
</html>`;

        await addDoc(collection(db, "mail"), {
            to: emailConfig.to,
            message: {
                subject: emailConfig.subject || "Potwierdzenie rejestracji",
                text: personalizedMessage,   // plain-text fallback
                html: htmlMessage,           // HTML version with images
            },
        });
    }

    return registrationId;
}
